'use client';

/**
 * Three-region hero — feature panel + centre slider + rail of static
 * "watch next" picks. Ported from ThemeMaker's
 * {@code blocks/hero/HeroFeatureRail}.
 *
 * <pre>
 *   ┌─────────────┬─────────────────────────┬───────────┐
 *   │ FEATURED    │                         │ [thumb]   │
 *   │ Big title   │   Slide image           │ Title     │
 *   │ description │                         │ Channel   │
 *   │ [▶ Play]    │   (auto-advances every  ├───────────┤
 *   │ • • • •     │    N seconds)           │ [thumb]   │
 *   └─────────────┴─────────────────────────┴───────────┘
 * </pre>
 *
 * <p>The left panel and the centre region are SYNCHRONISED — when
 * slide N is active, the panel shows that slide's title /
 * description / CTA and the centre shows that slide's image. The
 * right rail is INDEPENDENT — a static list of up to 4 items.
 *
 * <h3>Auto-advance</h3>
 * Opt-in via {@code autoPlay}. Suspends when
 * {@code prefers-reduced-motion} is set or the tab is hidden.
 *
 * <h3>Backwards compat</h3>
 * Tenants who configured the previous flat {@code image}/{@code title}/
 * {@code description}/{@code ctaLabel}/{@code ctaHref} shape still
 * work — those props are folded into a single synthetic slide.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Star, Play } from 'lucide-react';
import { tx } from '../_shared/coerce';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  useBlockPart,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { Button, Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroFeatureRailItem {
  title?: Localized;
  channel?: Localized;
  image?: string;
  href?: string;
  duration?: string;
}

export interface HeroFeatureRailSlide extends CampaignAudience {
  kicker?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  /** Static image used as the slide background. */
  image?: string;
}

export interface HeroFeatureRailProps {
  // Slider props (preferred)
  slides?: HeroFeatureRailSlide[];
  /** Auto-advance through slides. Default false. */
  autoPlay?: boolean;
  /** Seconds between advances. Default 6. */
  autoPlayInterval?: number;

  // Right-rail props
  items?: HeroFeatureRailItem[];

  // Backwards-compat: flat hero shape (no slides) gets folded into a
  // synthetic single slide.
  kicker?: Localized;
  title?: Localized;
  description?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  image?: string;
  /** Decorative dot count when using the flat shape (no real slides). */
  dotCount?: number;
}

export function HeroFeatureRail({
  slides,
  autoPlay = false,
  autoPlayInterval = 6,
  items = [],
  kicker,
  title,
  description,
  ctaLabel,
  ctaHref,
  image,
  dotCount,
}: HeroFeatureRailProps) {
  // Hooks first — must run in the same order on every render. Even
  // for the empty fallback we call all hooks above the early return.
  //
  // Prefer {@code slides[]}; otherwise fold the flat props into a
  // single slide so existing tenants don't break.
  const currentUser = useCurrentUser<CurrentUser>();
  const part = useBlockPart();
  const effectiveSlides: HeroFeatureRailSlide[] = useMemo(() => {
    const base =
      Array.isArray(slides) && slides.length > 0
        ? slides
        : [{ kicker, title, description, ctaLabel, ctaHref, image }];
    return base.filter((s) => isVisible(s, currentUser));
  }, [slides, kicker, title, description, ctaLabel, ctaHref, image, currentUser]);

  const [active, setActive] = useState(0);

  // Wrap when slides change length (e.g. an author deletes a slide).
  useEffect(() => {
    if (active >= effectiveSlides.length) {
      setActive(0);
    }
  }, [active, effectiveSlides.length]);

  // Auto-advance honouring prefers-reduced-motion + tab visibility.
  const reducedMotionRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    const onChange = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
    };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const autoPlaySeconds = Math.max(2, autoPlayInterval);

  useEffect(() => {
    if (!autoPlay) return;
    if (effectiveSlides.length <= 1) return;
    const intervalMs = autoPlaySeconds * 1000;
    const id = window.setInterval(() => {
      if (reducedMotionRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setActive((i) => (i + 1) % effectiveSlides.length);
    }, intervalMs);
    return () => {
      window.clearInterval(id);
    };
  }, [autoPlay, autoPlaySeconds, effectiveSlides.length]);

  const slide = effectiveSlides[Math.min(active, effectiveSlides.length - 1)] ?? {};
  const railItems = (items ?? []).slice(0, 5);
  const dotsCount = effectiveSlides.length > 1 ? effectiveSlides.length : (dotCount ?? 0);
  const showProgress = autoPlay && effectiveSlides.length > 1;

  return (
    <BlockRoot
      as="section"
      style={{
        padding: 'var(--space-6, 24px) clamp(var(--space-4, 16px), 3vw, var(--space-8, 32px))',
      }}
    >
      <Card
        className="hfr-grid"
        style={{
          ...cardReset,
          background: 'var(--color-surface-card, #fff)',
          borderRadius: 'var(--radius-lg, 18px)',
          padding: 'var(--space-4, 16px)',
          display: 'grid',
          gridTemplateColumns: 'minmax(260px, 0.95fr) minmax(0, 2fr) minmax(220px, 0.85fr)',
          gap: 'var(--space-4, 16px)',
          alignItems: 'start',
        }}
      >
        {/* LEFT — feature panel. Stretches to match the row's full
         *  height (tallest sibling); CTA + dots are pinned to the
         *  bottom via the spacer below. */}
        <div
          style={{
            alignSelf: 'stretch',
            background: 'var(--color-surface-muted, #f3f3f5)',
            borderRadius: 'var(--radius-md, 14px)',
            padding: 'clamp(var(--space-5, 20px), 2.5vw, var(--space-8, 32px))',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3-5, 14px)',
          }}
        >
          {/* Slide-derived content fades in on change; re-keying by
           *  `active` restarts the CSS animation each time. */}
          <div
            key={active}
            className="hfr-fade"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3-5, 14px)',
              flex: 1,
            }}
          >
            {slide.kicker && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--space-1-5, 6px)',
                  fontSize: 'var(--font-size-xs, 12px)',
                  fontWeight: 'var(--font-weight-semibold, 600)',
                  letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                  color: 'var(--color-text-secondary, #6b6b73)',
                }}
              >
                <Star size={12} fill="currentColor" stroke="none" aria-hidden />
                {tx(slide.kicker)}
              </div>
            )}
            {slide.title && (
              <h2
                {...part('title')}
                style={{
                  margin: 0,
                  fontSize: 'clamp(28px, 3.4vw, 44px)',
                  fontWeight: 'var(--font-weight-extrabold, 800)',
                  lineHeight: 'var(--line-height-tight, 1.05)',
                  letterSpacing: 'var(--font-tracking-snug, -0.015em)',
                  color: 'var(--color-text-primary, #111113)',
                  fontFamily: 'var(--font-family-display, inherit)',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {tx(slide.title)}
              </h2>
            )}
            {slide.description && (
              <p
                {...part('subtitle')}
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm, 14px)',
                  lineHeight: 'var(--line-height-relaxed, 1.55)',
                  color: 'var(--color-text-secondary, #6b6b73)',
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {tx(slide.description)}
              </p>
            )}

            <div style={{ flex: 1 }} />

            {slide.ctaLabel && (
              <Button
                href={slide.ctaHref ?? '#'}
                radius="pill"
                {...part('cta')}
                style={{
                  alignSelf: 'flex-start',
                  gap: 'var(--space-2-5, 10px)',
                  height: 'auto',
                  padding: 'var(--space-3, 12px) var(--space-5, 20px)',
                  background: 'var(--color-surface-card, #fff)',
                  color: 'var(--color-text-primary, #111113)',
                  boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(15,18,24,0.06))',
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'var(--space-5-5, 22px)',
                    height: 'var(--space-5-5, 22px)',
                    borderRadius: 'var(--radius-full, 999px)',
                    background: 'var(--color-text-primary, #111113)',
                    color: 'var(--color-surface-card, #fff)',
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  <Play size={10} fill="currentColor" stroke="none" />
                </span>
                {tx(slide.ctaLabel)}
              </Button>
            )}
          </div>

          {dotsCount > 0 && (
            <div
              {...part('tablist')}
              style={{
                display: 'flex',
                gap: 'var(--space-1-5, 6px)',
                alignItems: 'center',
                marginTop: 'var(--space-1, 4px)',
              }}
            >
              {Array.from({ length: dotsCount }).map((_, i) => {
                const isActive = i === active && effectiveSlides.length > 1;
                const decorative = effectiveSlides.length <= 1;
                return decorative ? (
                  <span
                    key={i}
                    aria-hidden
                    style={{
                      width: i === 0 ? 'var(--space-4-5, 18px)' : 'var(--space-1-5, 6px)',
                      height: 'var(--space-1-5, 6px)',
                      borderRadius: 'var(--radius-pill, 9999px)',
                      background:
                        i === 0
                          ? 'var(--color-text-primary, #111113)'
                          : 'var(--color-neutral-300, #dcdce0)',
                    }}
                  />
                ) : (
                  <Button
                    key={i}
                    type="button"
                    size="icon"
                    variant="ghost"
                    radius="pill"
                    aria-label={`Go to slide ${i + 1}`}
                    aria-current={isActive ? 'true' : undefined}
                    onClick={() => setActive(i)}
                    {...part('tab', i)}
                    // size="icon" already gives a 36x36 hit area — the small
                    // visual dot goes on an inner span instead of shrinking
                    // the button itself below the 24x24 touch-target minimum.
                    style={{
                      width: 24,
                      height: 24,
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'block',
                        width: isActive ? 'var(--space-4-5, 18px)' : 'var(--space-1-5, 6px)',
                        height: 'var(--space-1-5, 6px)',
                        borderRadius: 'var(--radius-pill, 9999px)',
                        background: isActive
                          ? 'var(--color-text-primary, #111113)'
                          : 'var(--color-neutral-300, #dcdce0)',
                        transition:
                          'width var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease), background var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease)',
                      }}
                    />
                  </Button>
                );
              })}
            </div>
          )}

          {showProgress && (
            <div
              style={{
                width: '100%',
                height: 'var(--space-0-5, 2px)',
                borderRadius: 'var(--radius-pill, 9999px)',
                background: 'var(--color-neutral-200, #ececef)',
                overflow: 'hidden',
                marginTop: 'var(--space-2, 8px)',
              }}
            >
              <div
                key={active}
                className="hfr-progress"
                aria-hidden
                style={{
                  height: '100%',
                  width: '100%',
                  transformOrigin: 'left',
                  background: 'var(--color-brand-500, #3b5bdb)',
                  ['--hfr-progress-duration' as string]: `${autoPlaySeconds}s`,
                }}
              />
            </div>
          )}
        </div>

        {/* Slide visual — below the text panel. <a> when ctaHref set
         *  so the whole image is clickable; <div> otherwise. Source
         *  images are pre-cropped to 16:9 (see seed data), so
         *  object-fit: cover fills the box exactly with no letterbox
         *  gap while still matching the declared aspect-ratio. */}
        {slide.ctaHref ? (
          <PortalLink
            href={slide.ctaHref}
            aria-label={tx(slide.title) || 'Open slide'}
            style={{
              display: 'block',
              width: '100%',
              aspectRatio: '16 / 9',
              borderRadius: 'var(--radius-md, 14px)',
              overflow: 'hidden',
              position: 'relative',
              background: 'var(--color-surface-muted, #f3f3f5)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {slide.image && (
              <img
                key={active}
                src={resizeImageUrl(slide.image, '1400x0')}
                alt=""
                decoding="async"
                className="hfr-fade-img"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {effectiveSlides.length === 0 && (
              <HeroEmptyFallback style={{ position: 'absolute', inset: 0 }} />
            )}
          </PortalLink>
        ) : (
          <div
            style={{
              width: '100%',
              aspectRatio: '16 / 9',
              background: 'var(--color-surface-muted, #f3f3f5)',
              borderRadius: 'var(--radius-md, 14px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {slide.image && (
              <img
                key={active}
                src={resizeImageUrl(slide.image, '1400x0')}
                alt=""
                decoding="async"
                className="hfr-fade-img"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {effectiveSlides.length === 0 && (
              <HeroEmptyFallback style={{ position: 'absolute', inset: 0 }} />
            )}
          </div>
        )}

        {/* RIGHT — rail of static items. Grid so the tablet breakpoint
         *  can switch it to 2 columns via CSS below. */}
        <div
          className="hfr-rail"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 'var(--space-3, 12px)',
          }}
        >
          {railItems.map((item, i) => (
            <PortalLink
              key={i}
              href={item.href ?? '#'}
              className="hfr-rail-item"
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 1fr',
                gap: 'var(--space-2-5, 10px)',
                padding: 'var(--space-1-5, 6px)',
                borderRadius: 'var(--radius-md, 12px)',
                background: 'var(--color-surface-card, #fff)',
                border: '1px solid var(--color-neutral-200, #ececef)',
                color: 'inherit',
                textDecoration: 'none',
                alignItems: 'center',
                flex: 1,
                minHeight: 'var(--space-18, 72px)',
              }}
            >
              <div
                style={{
                  aspectRatio: '16 / 9',
                  borderRadius: 'var(--radius-sm, 8px)',
                  background: 'var(--color-surface-muted, #ececef)',
                  backgroundImage: item.image ? `url("${item.image}")` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {item.duration && (
                  <span
                    style={{
                      position: 'absolute',
                      right: 'var(--space-1, 4px)',
                      bottom: 'var(--space-1, 4px)',
                      background: 'rgba(0,0,0,0.78)',
                      color: 'var(--color-text-on-brand, #fff)',
                      fontSize: 'var(--font-size-xs, 12px)',
                      fontWeight: 'var(--font-weight-semibold, 600)',
                      padding: 'var(--space-px, 1px) var(--space-1, 4px)',
                      borderRadius: 'var(--radius-sm, 4px)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {item.duration}
                  </span>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                {item.title && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs, 13px)',
                      fontWeight: 'var(--font-weight-semibold, 600)',
                      lineHeight: 'var(--line-height-normal, 1.3)',
                      color: 'var(--color-text-primary, #111113)',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {tx(item.title)}
                  </div>
                )}
                {item.channel && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs, 12px)',
                      color: 'var(--color-text-secondary, #6b6b73)',
                      marginTop: 'var(--space-0-5, 2px)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {tx(item.channel)}
                  </div>
                )}
              </div>
            </PortalLink>
          ))}
        </div>
      </Card>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @media (max-width: 1100px) {
          .hfr-grid { grid-template-columns: 1fr !important; }
          .hfr-rail { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 560px) {
          .hfr-rail { grid-template-columns: 1fr !important; }
        }

        @keyframes hfr-fade-in {
          from { opacity: 0; transform: translateY(var(--space-1-5, 6px)); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hfr-fade {
          animation: hfr-fade-in var(--motion-duration-normal, 320ms) var(--motion-ease-out, ease) both;
        }

        /* Opacity-only fade for the slide image — it's absolutely
         *  positioned with inset: 0 to cover its box exactly, so any
         *  translateY would momentarily uncover a background-colored
         *  gap at the edge it moves away from. */
        @keyframes hfr-fade-in-img {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .hfr-fade-img {
          animation: hfr-fade-in-img var(--motion-duration-normal, 320ms) var(--motion-ease-out, ease) both;
        }

        @keyframes hfr-progress-fill {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
        .hfr-progress {
          animation: hfr-progress-fill linear forwards;
          animation-duration: var(--hfr-progress-duration, 6s);
        }

        .hfr-rail-item {
          transition:
            transform var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease),
            box-shadow var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease),
            border-color var(--motion-duration-fast, 160ms) var(--motion-ease-out, ease);
        }
        .hfr-rail-item:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md, 0 6px 16px rgba(15,18,24,0.1));
          border-color: var(--color-border-strong, #d4d4d8);
        }

        @media (prefers-reduced-motion: reduce) {
          .hfr-fade { animation: none; }
          .hfr-fade-img { animation: none; }
          .hfr-progress { animation: none; transform: scaleX(1); }
          .hfr-rail-item { transition: none; }
        }
      `,
        }}
      />
    </BlockRoot>
  );
}
